import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useStore } from "../store/store";
import { useT } from "../i18n/useT";
import { getDisclosure, renderDisclosure } from "@mensajeando/compliance";

export function CampaignDetail() {
  const { id } = useParams();
  const t = useT();
  const campaign = useStore((s) => s.campaigns.find((c) => c.id === id));
  const updateCampaign = useStore((s) => s.updateCampaign);
  const flows = useStore((s) => s.flows);
  const orgs = useStore((s) => s.organizations);
  const clients = useStore((s) => s.clients);

  const flow = campaign ? flows[campaign.flowId] : undefined;
  const client = clients.find((c) => c.id === campaign?.clientId);
  const org = orgs.find((o) => o.id === campaign?.organizationId);

  const previewEn = useMemo(() => {
    if (!campaign || !client || !org) return undefined;
    const d = getDisclosure(campaign.type, campaign.channel, "en");
    return d ? renderDisclosure(d, { clientName: client.name, supportEmail: org.supportEmail }) : undefined;
  }, [campaign, client, org]);
  const previewEs = useMemo(() => {
    if (!campaign || !client || !org) return undefined;
    const d = getDisclosure(campaign.type, campaign.channel, "es");
    return d ? renderDisclosure(d, { clientName: client.name, supportEmail: org.supportEmail }) : undefined;
  }, [campaign, client, org]);

  if (!campaign) return <p>Not found. <Link to="/app/campaigns" className="text-brand-700">Back</Link></p>;

  return (
    <div className="max-w-4xl">
      <Link to="/app/campaigns" className="text-sm text-slate-500 hover:text-slate-700">← {t.common.back}</Link>
      <h1 className="text-2xl font-semibold mt-2">{campaign.name}</h1>
      <p className="text-sm text-slate-500">{t.campaign.types[campaign.type]} · {t.campaign.channels[campaign.channel]}</p>

      <section className="grid grid-cols-2 gap-4 mt-6">
        <Toggle
          label={t.campaign.registration}
          on={campaign.campaignRegistered}
          onChange={(v) => updateCampaign(campaign.id, { campaignRegistered: v })}
          help="The Campaign Registry (10DLC) or WhatsApp Business platform approval. Required to send."
        />
        <Toggle
          label="Sender eligibility"
          on={campaign.senderEligible}
          onChange={(v) => updateCampaign(campaign.id, { senderEligible: v })}
          help="Number/sender ID approved for this use case + channel."
        />
        <Toggle
          label="Require consent"
          on={campaign.requiresConsent}
          onChange={(v) => updateCampaign(campaign.id, { requiresConsent: v })}
          help="Strongly recommended. Disabling is only legal for narrow transactional carve-outs."
        />
        <div className="card card-pad">
          <div className="label">Status</div>
          <select
            className="input"
            value={campaign.status}
            onChange={(e) => updateCampaign(campaign.id, { status: e.target.value as typeof campaign.status })}
          >
            <option value="draft">draft</option>
            <option value="live">live</option>
            <option value="paused">paused</option>
            <option value="complete">complete</option>
          </select>
        </div>
      </section>

      <section className="mt-6 grid grid-cols-2 gap-4">
        <DisclosurePreview title={`${t.campaign.cta} — English`} d={previewEn} />
        <DisclosurePreview title={`${t.campaign.cta} — Español`} d={previewEs} />
      </section>

      <section className="card card-pad mt-6">
        <h2 className="font-semibold mb-2">Flow</h2>
        {flow ? (
          <>
            <p className="text-sm text-slate-600 mb-2">
              {flow.name} · v{flow.version} · {Object.keys(flow.nodes).length} nodes
            </p>
            <ul className="text-sm space-y-1">
              {Object.values(flow.nodes).map((n) => (
                <li key={n.id} className="flex items-center gap-2">
                  <span className="badge-muted">{n.kind}</span>
                  <span className="font-medium">{n.label}</span>
                  <span className="text-slate-400">({n.id})</span>
                </li>
              ))}
            </ul>
            <Link to="/app/flows" className="btn-secondary mt-3 inline-flex">Edit flow</Link>
          </>
        ) : <p className="text-sm text-slate-500">No flow attached.</p>}
      </section>
    </div>
  );
}

function Toggle({ label, on, onChange, help }: { label: string; on: boolean; onChange: (v: boolean) => void; help?: string }) {
  return (
    <div className="card card-pad flex items-start justify-between gap-3">
      <div>
        <div className="font-medium text-sm">{label}</div>
        {help && <div className="text-xs text-slate-500 mt-0.5">{help}</div>}
      </div>
      <button
        onClick={() => onChange(!on)}
        className={`h-6 w-11 rounded-full transition ${on ? "bg-brand-600" : "bg-slate-300"} relative shrink-0`}
        aria-pressed={on}
      >
        <span className={`absolute top-0.5 ${on ? "left-5" : "left-0.5"} h-5 w-5 rounded-full bg-white shadow transition-all`} />
      </button>
    </div>
  );
}

function DisclosurePreview({ title, d }: { title: string; d?: { cta: string; helpReply: string; stopReply: string; optInConfirmation: string } }) {
  if (!d) return null;
  return (
    <div className="card card-pad text-sm">
      <h3 className="font-semibold mb-2">{title}</h3>
      <Item label="CTA" value={d.cta} />
      <Item label="HELP reply" value={d.helpReply} />
      <Item label="STOP reply" value={d.stopReply} />
      <Item label="Opt-in confirmation" value={d.optInConfirmation} />
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="text-slate-800">{value}</div>
    </div>
  );
}
